import {
  Product,
  Return,
  ReturnType,
  ShipmentGroup,
} from "@redotech/redo-model/return";
import { Team } from "@redotech/redo-model/team";
import { GetUser, Permission, permitted } from "@redotech/redo-model/user";

export function answerName(answer: string) {
  return answer[0].toUpperCase() + answer.slice(1);
}

export function shipmentStatusName(status: string) {
  if (!status || status === "unknown") {
    return "-";
  }
  if (status === "pre_transit") {
    return "Pre-transit";
  }
  return status[0].toUpperCase() + status.slice(1).replace(/_/g, " ");
}

export function returnStatusName(status: string) {
  return status[0].toUpperCase() + status.slice(1).replace(/_/g, " ");
}

export function returnKindNameProduct(
  product: Product,
  provision?: string,
  returnType?: string,
): string {
  const isExchange = isVariantExchange(product);
  if (returnType && returnType === "claim" && isExchange) {
    return "Replace";
  }
  if (isExchange) {
    return "Exchange";
  }
  return (
    product.strategy[0].toUpperCase() +
    product.strategy.slice(1).replace(/_/g, " ")
  );
}

export function returnKindNameReturn(
  return_: Return,
  abbreviate: boolean = true,
) {
  if (return_?.advancedExchangeItems?.length) {
    if (return_.provision === "instant") {
      return "Instant Exchange";
    } else {
      return "Exchange";
    }
  }
  const uniqueTypes = new Set(
    return_.products.map((product) =>
      returnKindNameProduct(product, return_.provision, return_.type),
    ),
  );
  const types = [...uniqueTypes].map((returnType) => {
    if (returnType === "Exchange" && return_.provision === "instant") {
      return "Instant Exchange";
    } else {
      return returnType;
    }
  });

  if (abbreviate) {
    let [type] = types.sort();
    if (1 < types.length) {
      type += " …";
    }
    return type;
  } else {
    return types.sort().join(", ");
  }
}

export function isVariantExchange(product: Product): boolean {
  return !!product.exchange_for || !!product.exchangeGroupItem;
}

export function itemStatusName(status: string) {
  return status === "complete"
    ? "Processed"
    : status === "rejected"
      ? "Rejected"
      : status === "cancelled"
        ? "Cancelled"
        : status === "pending"
          ? "Pending"
          : status === "pre-shipment"
            ? "Pre-Shipment"
            : "Unprocessed";
}

export function orderStatusName(shopifyFullfillmentStatus: string) {
  switch (shopifyFullfillmentStatus) {
    case null:
      return "Unfulfilled";
    case "partial":
      return "Partially fulfilled";
    default:
      return (
        shopifyFullfillmentStatus[0].toUpperCase() +
        shopifyFullfillmentStatus.slice(1)
      );
  }
}

export function hasEditReturnPermissions(team?: Team, user?: GetUser) {
  if (!team || !user) {
    return false;
  }
  if (team.storeUrl === "shopthemint.myshopify.com") {
    return user.roles.includes("admin");
  }
  return permitted(user.permissions, Permission.MANAGE_RETURN);
}

export function isExchangeOrderEditable(
  return_: Return,
  team: Team | undefined,
  user: GetUser | undefined,
) {
  const isProcessed = ["complete", "rejected"].includes(return_.status);
  const exchangeOrderExists =
    !!return_.exchangeOrder.length || !!return_.draftOrderId;

  return (
    !!hasEditReturnPermissions(team, user) &&
    !return_.instantRefund &&
    !isProcessed &&
    !exchangeOrderExists &&
    (!team ||
      team.accessScope.includes("unauthenticated_read_product_listings"))
  );
}

export function hasNewOrderItems(return_: Return) {
  return (
    return_.products.some((product) => isVariantExchange(product)) ||
    return_.advancedExchangeItems.length > 0
  );
}

export const returnTypeName = (returnType?: ReturnType, titleCase = false) =>
  titleCase ? (returnType === "return" ? "Return" : "Claim") : returnType;

export function getProductsByShippingGroupTitle(return_: Return) {
  const productsByGroupName: { groupName: string; products: Product[] }[] = [];

  if (return_?.shipmentGroups?.length) {
    for (const product of return_.products) {
      const allGroups = return_.shipmentGroups;
      const shipmentGroupsIDs = product.shipmentGroupIDs?.length
        ? product.shipmentGroupIDs
        : product.shipmentGroupID
          ? [product.shipmentGroupID]
          : [];
      const shipmentGroups: ShipmentGroup[] = return_.shipmentGroups.filter(
        (group) => shipmentGroupsIDs.includes(group.id),
      );
      const shipmentIndices: number[] = shipmentGroups
        .map((group) => allGroups.indexOf(group))
        .sort();

      const groupName = `Shipment ${shipmentIndices.map((ind) => ind + 1).join(" + ")}`;

      const productGroup = productsByGroupName.find(
        (group) => group.groupName === groupName,
      );
      if (productGroup) {
        productGroup?.products.push(product);
      } else {
        productsByGroupName.push({ groupName, products: [product] });
      }
    }
  }

  return productsByGroupName;
}
